import React, {
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
} from 'react'
import { Button } from '~/lib/ui/button'
import { useMessaging } from './MessagingProvider'
import CoachMessage from './CoachMessage'
import ThreadReply from './ThreadReply'
import { apiGet } from '~/util/api'
import { useQuery } from '@tanstack/react-query'
import useTranslation from '~/components/shared/TranslationProvider'
import { cn } from '~/lib/utils'

const ReplyContext = createContext()

const CoachMessageThread = ({ layoutHeight }) => {
  const { t } = useTranslation('CoachMessageThread')
  const { currentThreadId, isMobile, locale, userIsCoach } = useMessaging()

  const headerRef = useRef(null)
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const update = () =>
      setHeaderHeight(Math.ceil(el.getBoundingClientRect().height))
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const queryFn = () => {
    return apiGet(`${location.pathname}/${currentThreadId}.json`).then(
      (res) => res.data,
    )
  }

  const {
    error,
    data: messageThread,
    isPending,
  } = useQuery({
    queryKey: ['messageThread', currentThreadId],
    queryFn,
    enabled: !!currentThreadId,
  })

  if (!currentThreadId) return <div className="h-full"></div>

  if (isPending) return <div className="h-full">{t('loading')}</div>

  if (error) {
    console.error('Error fetching message thread:', error)
    return 'An error has occurred'
  }

  const {
    coach_messages: messages,
    coach,
    candidate_name: candidateName,
    link_to_candidate: linkToCandidate,
    subject,
    liaison_image_url: liaisonImageUrl,
    is_liaison: coachIsLiaison,
    automated,
    country_language: countryLanguage,
    country_flag: countryFlag,
  } = messageThread

  const ViewComp = isMobile ? MobileView : DesktopView

  const keptFilter = userIsCoach
    ? (message) => !message.coach_deleted_at
    : (message) => !message.candidate_deleted_at
  const displayMessages = [...(messages || [])].reverse().filter(keptFilter)

  const messageProps = {
    displayMessages,
    coach,
    candidateName,
    linkToCandidate,
    liaisonImageUrl,
    coachIsLiaison,
    automated,
    countryLanguage,
    countryFlag,
    t,
  }

  return (
    <div className="mt-8 flex flex-col overflow-hidden rounded-3xl border border-silver bg-white p-4">
      <div
        ref={headerRef}
        className="relative -mx-4 -mt-4 flex items-center justify-between border-b bg-intoo-green-coachcta-lighter py-4 pl-6 pr-4"
      >
        <div className="font-roboto-slab text-intoo-green-medium md:text-2xl">
          {t('subject_line')} {subject}
        </div>
      </div>
      <ReplyProvider messageThread={messageThread}>
        <ViewComp layoutHeight={layoutHeight} headerHeight={headerHeight}>
          <CoachMessages locale={locale} {...messageProps} />
        </ViewComp>
      </ReplyProvider>
    </div>
  )
}

const ReplyProvider = ({ messageThread, children }) => {
  const { coach, userIsCoach } = useMessaging()
  const {
    candidate_name: candidateName,
    may_reply: mayReply,
    coach_deleted_at: coachDeletedAt,
    candidate_deleted_at: candidateDeletedAt,
    country_language: countryLanguage,
    country_flag: countryFlag,
  } = messageThread || {}
  const coachName = [coach?.first_name, coach?.last_name].join(' ')
  const recipientName = userIsCoach ? candidateName : coachName
  const isDeletedByOther = !!(userIsCoach ? candidateDeletedAt : coachDeletedAt)

  return (
    <ReplyContext.Provider
      value={{
        recipientName,
        isDeletedByOther,
        mayReply,
        countryLanguage,
        countryFlag,
      }}
    >
      {children}
    </ReplyContext.Provider>
  )
}

const MobileView = ({ children }) => {
  return (
    <div className="flex flex-col py-2">
      <ThreadReplyToggle className="my-4" />
      <div>{children}</div>
    </div>
  )
}

const DesktopView = ({ children, layoutHeight, headerHeight }) => {
  // panel p-4 (32) + outer div mt-8 (32) + outer div p-4 bottom (16) = 80px fixed overhead
  // header -mt-4 reclaims the top 16px of outer p-4, so it nets out
  const contentMaxHeight = layoutHeight
    ? Math.max(layoutHeight - 80 - headerHeight, 100)
    : undefined

  return (
    <div
      className="flex min-h-0 flex-col"
      style={{ maxHeight: contentMaxHeight }}
    >
      <div
        className="flex min-h-0 shrink-0 flex-col items-end pt-2"
        style={{ maxHeight: contentMaxHeight }}
      >
        <ThreadReplyToggle className="mb-0 mt-2 w-auto" />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pl-2 pr-3">
        <div className="mt-4">{children}</div>
      </div>
    </div>
  )
}

const ThreadReplyToggle = ({ className }) => {
  const { userIsCoach } = useMessaging()
  const [replying, setReplying] = useState(false)
  const { t } = useTranslation('CoachMessageThread')
  const {
    recipientName: name,
    isDeletedByOther,
    mayReply,
    countryLanguage,
    countryFlag,
  } = useContext(ReplyContext)
  if (isDeletedByOther)
    return (
      <div className="mt-4 w-full border-b border-silver pb-4">
        <div className="text-xl font-medium">
          {t('archived_thread', { name })}
        </div>
        <div className="text-sm">{t('archived_thread_detail')}</div>
      </div>
    )

  if (replying) {
    return (
      <div className="mb-4 flex min-h-0 w-full flex-col border-b border-silver pb-4">
        <h2 className="shrink-0">{t('replying_to', { name })}</h2>
        {userIsCoach && (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-sm text-mine-shaft-500">
              {countryLanguage}
            </span>
            <i className={`glyphicon mb-1 bfh-flag-${countryFlag}`}></i>
          </div>
        )}
        <ThreadReply setReplying={setReplying} name={name} />
      </div>
    )
  } else if (mayReply) {
    return (
      <Button
        onClick={() => setReplying(true)}
        variant="coachcta"
        className={cn('mb-4 w-full border-b border-silver', className)}
      >
        {t('reply_to', { name })}
      </Button>
    )
  }
}

const CoachMessages = ({
  displayMessages,
  coach,
  candidateName,
  linkToCandidate,
  liaisonImageUrl,
  coachIsLiaison,
  automated,
  countryLanguage,
  countryFlag,
  locale,
  t,
}) => {
  const { userIsCoach } = useMessaging()
  return (
    <>
      {displayMessages?.map((message) => (
        <CoachMessage
          key={message.id}
          message={message}
          coach={coach}
          candidateName={candidateName}
          userIsCoach={userIsCoach}
          linkToCandidate={linkToCandidate}
          liaisonImageUrl={liaisonImageUrl}
          coachIsLiaison={coachIsLiaison}
          automated={automated}
          countryLanguage={countryLanguage}
          countryFlag={countryFlag}
          locale={locale}
          t={t}
        />
      ))}
    </>
  )
}

export default CoachMessageThread
